import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';

export interface SeoConfig {
    title: string;
    description: string;
    keywords?: string;
    robots?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    ogUrl?: string;
    twitterCard?: string;
}

@Injectable({
    providedIn: 'root'
})
export class SeoService {
    private baseUrl = 'https://quickindexing.com';
    private defaultConfig: SeoConfig = {
        title: 'QuickIndexing – Instant URL Indexer & Backlink Indexing Tool',
        description: 'Get your URLs and backlinks indexed by Google within minutes or 2 min. QuickIndexing is the fastest instant indexing tool with 99.9% uptime and real-time tracking.',
        keywords: 'instant indexing tool, backlink indexer, url indexer, fast google indexing, get backlinks indexed, quick indexing',
        robots: 'index, follow'
    };

    constructor(
        private titleService: Title,
        private metaService: Meta,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private document: Document
    ) { }

    /**
     * Initialize SEO service - call this from app.component.ts
     */
    init(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.activatedRoute),
            map(route => {
                while (route.firstChild) {
                    route = route.firstChild;
                }
                return route;
            }),
            mergeMap(route => route.data)
        ).subscribe(data => {
            const seoData = data as SeoConfig;
            this.updateSeoTags(seoData);
            this.setAutoBreadcrumbs();
        });
    }

    /**
     * Update all SEO tags based on route data
     */
    updateSeoTags(config: Partial<SeoConfig>): void {
        const mergedConfig = { ...this.defaultConfig, ...config };

        // Update title
        this.titleService.setTitle(mergedConfig.title);

        // Update meta tags
        this.updateMetaTag('description', mergedConfig.description);
        this.updateMetaTag('keywords', mergedConfig.keywords || '');
        this.updateMetaTag('robots', mergedConfig.robots || 'index, follow');

        // Update Open Graph tags
        this.updateMetaTag('og:title', mergedConfig.ogTitle || mergedConfig.title, 'property');
        this.updateMetaTag('og:description', mergedConfig.ogDescription || mergedConfig.description, 'property');
        this.updateMetaTag('og:url', mergedConfig.ogUrl || this.getCurrentUrl(), 'property');
        this.updateMetaTag('og:type', 'website', 'property');
        this.updateMetaTag('og:site_name', 'QuickIndexing', 'property');
        const ogImage = (config as any).ogImage || mergedConfig.ogImage || `${this.baseUrl}/quick-indexing-og.png`;
        this.updateMetaTag('og:image', ogImage, 'property');

        // Update Twitter Card tags
        this.updateMetaTag('twitter:card', mergedConfig.twitterCard || 'summary_large_image');
        this.updateMetaTag('twitter:title', mergedConfig.ogTitle || mergedConfig.title);
        this.updateMetaTag('twitter:description', mergedConfig.ogDescription || mergedConfig.description);
        this.updateMetaTag('twitter:image', ogImage);

        // Update canonical URL
        this.setCanonicalUrl(this.getCurrentUrl());
    }

    /**
     * Update a single meta tag
     */
    private updateMetaTag(name: string, content: string, attr: 'name' | 'property' = 'name'): void {
        if (!content) return;

        const selector = attr === 'property' ? `property="${name}"` : `name="${name}"`;
        const existingTag = this.metaService.getTag(selector);

        if (existingTag) {
            this.metaService.updateTag({ [attr]: name, content });
        } else {
            this.metaService.addTag({ [attr]: name, content });
        }
    }

    /**
     * Set canonical URL
     */
    setCanonicalUrl(url?: string): void {
        const canonicalUrl = url || this.getCurrentUrl();

        // Remove existing canonical
        const existingCanonical = this.document.querySelector('link[rel="canonical"]');
        if (existingCanonical) {
            existingCanonical.remove();
        }

        // Add new canonical
        const link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonicalUrl);
        this.document.head.appendChild(link);
    }

    /**
     * Get current full URL
     */
    private getCurrentUrl(): string {
        const path = this.router.url.split('?')[0]; // Remove query params
        return `${this.baseUrl}${path === '/' ? '' : path}`;
    }

    /**
     * Manually set SEO for a page (useful for dynamic content like blog posts)
     */
    setPageSeo(config: Partial<SeoConfig>): void {
        this.updateSeoTags(config);
    }

    /**
     * Set JSON-LD structured data schema
     * @param schema - The schema object to inject
     * @param id - Optional unique identifier for the script tag (to allow updates)
     */
    setJsonLd(schema: object, id: string = 'structured-data'): void {
        // Remove existing script with same id
        const existingScript = this.document.getElementById(id);
        if (existingScript) {
            existingScript.remove();
        }

        // Create and inject new script
        const script = this.document.createElement('script');
        script.setAttribute('id', id);
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(schema);
        this.document.head.appendChild(script);
    }

    /**
     * Remove JSON-LD structured data by id
     */
    removeJsonLd(id: string = 'structured-data'): void {
        const existingScript = this.document.getElementById(id);
        if (existingScript) {
            existingScript.remove();
        }
    }

    /**
     * Set BreadcrumbList schema from the current route path.
     * Call this from components or from the route subscription.
     */
    setBreadcrumbs(items: { name: string; url: string }[]): void {
        const breadcrumbSchema = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': items.map((item, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'name': item.name,
                'item': item.url
            }))
        };
        this.setJsonLd(breadcrumbSchema, 'breadcrumb-schema');
    }

    /**
     * Auto-generate breadcrumbs from the current route path.
     */
    setAutoBreadcrumbs(): void {
        const path = this.router.url.split('?')[0];
        const segments = path.split('/').filter(s => s);

        const items: { name: string; url: string }[] = [
            { name: 'Home', url: this.baseUrl }
        ];

        let currentPath = '';
        for (const segment of segments) {
            currentPath += '/' + segment;
            const name = segment
                .replace(/-/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
            items.push({ name, url: `${this.baseUrl}${currentPath}` });
        }

        this.setBreadcrumbs(items);
    }

    /**
     * Set rel="next" and rel="prev" pagination links.
     * Pass null for prev/next if there is no previous/next page.
     */
    setPaginationLinks(prevUrl: string | null, nextUrl: string | null): void {
        // Remove existing
        const existingPrev = this.document.querySelector('link[rel="prev"]');
        const existingNext = this.document.querySelector('link[rel="next"]');
        if (existingPrev) existingPrev.remove();
        if (existingNext) existingNext.remove();

        if (prevUrl) {
            const link = this.document.createElement('link');
            link.setAttribute('rel', 'prev');
            link.setAttribute('href', prevUrl);
            this.document.head.appendChild(link);
        }
        if (nextUrl) {
            const link = this.document.createElement('link');
            link.setAttribute('rel', 'next');
            link.setAttribute('href', nextUrl);
            this.document.head.appendChild(link);
        }
    }

    /**
     * Remove pagination links (call on component destroy).
     */
    removePaginationLinks(): void {
        const existingPrev = this.document.querySelector('link[rel="prev"]');
        const existingNext = this.document.querySelector('link[rel="next"]');
        if (existingPrev) existingPrev.remove();
        if (existingNext) existingNext.remove();
    }
}
